export const MOSCOW_TIME_ZONE = 'Europe/Moscow'

const MOSCOW_OFFSET_HOURS = 3

interface IMoscowDateTimeParts {
    year: number;
    month: number;
    day: number;
    hours: number;
    minutes: number;
    seconds: number;
}

const moscowDateTimeFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: MOSCOW_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
})

const getPart = (parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): number => {
    return Number(parts.find(part => part.type === type)?.value ?? 0)
}

export const parseUtcDateTime = (value: string): Date => {
    const normalized = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value)
        ? value
        : `${value.replace(' ', 'T')}Z`

    return new Date(normalized)
}

export const getMoscowDateTimeParts = (value: Date | string): IMoscowDateTimeParts => {
    const date = value instanceof Date ? value : parseUtcDateTime(value)
    const parts = moscowDateTimeFormatter.formatToParts(date)

    return {
        year: getPart(parts, 'year'),
        month: getPart(parts, 'month'),
        day: getPart(parts, 'day'),
        hours: getPart(parts, 'hour'),
        minutes: getPart(parts, 'minute'),
        seconds: getPart(parts, 'second'),
    }
}

export const createUtcDateFromMoscow = (
    year: number,
    month: number,
    day: number,
    hours = 0,
    minutes = 0,
    seconds = 0,
): Date => {
    return new Date(Date.UTC(year, month - 1, day, hours - MOSCOW_OFFSET_HOURS, minutes, seconds))
}

export const createUtcDateFromMoscowInput = (dateValue: string, timeValue: string): Date | null => {
    const [year, month, day] = dateValue.split('-').map(Number)
    const [hours, minutes] = timeValue.split(':').map(Number)

    if(!year || !month || !day || Number.isNaN(hours) || Number.isNaN(minutes)) return null

    const date = createUtcDateFromMoscow(year, month, day, hours, minutes)
    const parts = getMoscowDateTimeParts(date)

    if(
        parts.year !== year || parts.month !== month || parts.day !== day ||
        parts.hours !== hours || parts.minutes !== minutes
    ) return null

    return date
}

export const createUtcDateFromMoscowDateTimeInput = (value: string): Date | null => {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/)
    if(!match) return null

    const [, year, month, day, hours, minutes, seconds] = match.map(Number)
    const date = createUtcDateFromMoscow(year, month, day, hours, minutes, seconds)
    const parts = getMoscowDateTimeParts(date)

    if(
        parts.year !== year || parts.month !== month || parts.day !== day ||
        parts.hours !== hours || parts.minutes !== minutes || parts.seconds !== seconds
    ) return null

    return date
}

export const createMoscowDayStart = (date: Date): Date => {
    return createUtcDateFromMoscow(date.getFullYear(), date.getMonth() + 1, date.getDate())
}

export const createMoscowSlotDate = (date: Date, slotIndex: number): Date => {
    const totalMinutes = slotIndex * 30

    return createUtcDateFromMoscow(
        date.getFullYear(),
        date.getMonth() + 1,
        date.getDate(),
        Math.floor(totalMinutes / 60),
        totalMinutes % 60,
    )
}

export const formatMoscowTime = (value: Date | string): string => {
    const date = value instanceof Date ? value : parseUtcDateTime(value)

    return date.toLocaleTimeString('ru-RU', {
        timeZone: MOSCOW_TIME_ZONE,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    })
}

export const formatMoscowDateInput = (value: Date | string): string => {
    const {year, month, day} = getMoscowDateTimeParts(value)

    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export const formatMoscowDateTimeInput = (value: Date | string): string => {
    const date = value instanceof Date ? value : parseUtcDateTime(value)

    return `${formatMoscowDateInput(date)} ${formatMoscowTimeInput(date)}:${String(getMoscowDateTimeParts(date).seconds).padStart(2, '0')}`
}

export const formatUtcDateTime = (value: Date): string => {
    return value.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, '')
}

export const formatMoscowTimeInput = (value: Date | string): string => {
    const {hours, minutes} = getMoscowDateTimeParts(value)

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

export const formatMoscowDate = (
    value: Date | string,
    options: Intl.DateTimeFormatOptions,
): string => {
    const date = value instanceof Date ? value : parseUtcDateTime(value)

    return date.toLocaleDateString('ru-RU', {...options, timeZone: MOSCOW_TIME_ZONE})
}

export const formatMoscowDateTime = (
    value: Date | string,
    options: Intl.DateTimeFormatOptions = {},
): string => {
    const date = value instanceof Date ? value : parseUtcDateTime(value)

    return date.toLocaleString('ru-RU', {...options, timeZone: MOSCOW_TIME_ZONE})
}

export const isSameMoscowDate = (first: Date | string, second: Date | string): boolean => {
    const firstParts = getMoscowDateTimeParts(first)
    const secondParts = getMoscowDateTimeParts(second)

    return firstParts.year === secondParts.year &&
        firstParts.month === secondParts.month &&
        firstParts.day === secondParts.day
}
